/**
 * useRealtimeSync - Hook for real-time WebSocket synchronization
 * Restaurant DAMAS - Système de caisse professionnel
 * 
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Connection status indicator
 * - Event handlers for all sync types
 * - Heartbeat/ping mechanism
 * - Force disconnect handling
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Connection states
export const ConnectionState = {
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    RECONNECTING: 'reconnecting',
    ERROR: 'error'
};

// Event types
export const SyncEventType = {
    // Stock
    STOCK_UPDATE: 'stock_update',
    
    // Tables
    TABLE_UPDATE: 'table_update',
    TABLES_REFRESH: 'tables_refresh',
    
    // Orders
    ORDER_UPDATE: 'order_update',
    ORDER_COMPLETED: 'order_completed',
    ORDER_NOTIFICATION: 'order_notification',
    
    // Kitchen/Bar
    KITCHEN_UPDATE: 'kitchen_update',
    BAR_UPDATE: 'bar_update',
    TICKET_UPDATE: 'ticket_update',
    
    // Cart
    CART_UPDATE: 'cart_update',
    CART_SYNC: 'cart_sync',
    
    // Register
    REGISTER_STATUS: 'register_status',
    REGISTER_CLOSED: 'register_closed',
    SESSION_UPDATE: 'session_update',
    
    // Admin
    ADMIN_UPDATE: 'admin_update',
    PRODUCT_UPDATE: 'product_update',
    FORCE_REFRESH: 'force_refresh',
    
    // System
    INITIAL_SYNC: 'initial_sync',
    GLOBAL_CONFIG: 'global_config',
    PONG: 'pong'
};

const DEFAULT_OPTIONS = {
    reconnectInterval: 1000,      // Start with 1 second
    maxReconnectInterval: 30000,  // Max 30 seconds
    reconnectDecay: 1.5,          // Exponential backoff multiplier
    maxReconnectAttempts: 50,     // Max attempts before giving up
    pingInterval: 25000,          // Ping every 25 seconds
    debug: false
};

export const useRealtimeSync = (options = {}) => {
    const { user } = useAuth();
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    // State
    const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
    const [lastMessage, setLastMessage] = useState(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    
    // Refs
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);
    const eventHandlersRef = useRef({});
    const currentReconnectInterval = useRef(opts.reconnectInterval);
    
    // Get WebSocket URL
    const wsUrl = useMemo(() => {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = baseUrl.replace(/^https?:\/\//, '');
        // Use /api/ws/pos for proper Kubernetes ingress routing
        return `${wsProtocol}://${wsHost}/api/ws/pos`;
    }, []);
    
    // Register event handler
    const on = useCallback((eventType, handler) => {
        if (!eventHandlersRef.current[eventType]) {
            eventHandlersRef.current[eventType] = [];
        }
        eventHandlersRef.current[eventType].push(handler);
        
        // Return unsubscribe function
        return () => {
            eventHandlersRef.current[eventType] = 
                eventHandlersRef.current[eventType].filter(h => h !== handler);
        };
    }, []);
    
    // Remove event handler
    const off = useCallback((eventType, handler) => {
        if (eventHandlersRef.current[eventType]) {
            eventHandlersRef.current[eventType] = 
                eventHandlersRef.current[eventType].filter(h => h !== handler);
        }
    }, []);
    
    // Emit event to handlers
    const emit = useCallback((eventType, data) => {
        const handlers = eventHandlersRef.current[eventType] || [];
        handlers.forEach(handler => {
            try {
                handler(data);
            } catch (e) {
                console.error(`Error in ${eventType} handler:`, e);
            }
        });
    }, []);
    
    // Send message through WebSocket
    const send = useCallback((data) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(data));
            return true;
        }
        return false;
    }, []);
    
    // Send ping
    const sendPing = useCallback(() => {
        send({ type: 'ping', user_id: user?.id, timestamp: new Date().toISOString() });
    }, [send, user?.id]);
    
    // Handle incoming message
    const handleMessage = useCallback((event) => {
        try {
            const data = JSON.parse(event.data);
            setLastMessage(data);
            
            if (opts.debug) {
                console.log('[WS] Received:', data.type, data);
            }
            
            // Handle specific message types
            switch (data.type) {
                case SyncEventType.REGISTER_CLOSED:
                    // Force redirect when register is closed
                    toast.error(data.message || 'Cette caisse a été fermée.');
                    if (data.force_redirect) {
                        setTimeout(() => {
                            window.location.href = '/pos';
                        }, 2000);
                    }
                    break;
                    
                case SyncEventType.FORCE_REFRESH:
                    toast.info('Mise à jour des données en cours...');
                    // Emit event for components to refresh
                    break;
                    
                case SyncEventType.STOCK_UPDATE:
                    // Silent stock update - no toast
                    break;
                    
                case SyncEventType.ORDER_UPDATE:
                    if (data.action === 'ready') {
                        toast.success(`Commande #${data.order?.order_number || ''} prête !`);
                    }
                    break;
                    
                case SyncEventType.PONG:
                    // Connection is healthy
                    break;
                    
                default:
                    break;
            }
            
            // Emit to registered handlers
            emit(data.type, data);
            
        } catch (e) {
            console.error('[WS] Error parsing message:', e);
        }
    }, [emit, opts.debug]);
    
    // Connect to WebSocket
    const connect = useCallback(() => {
        if (!user?.id) {
            if (opts.debug) console.log('[WS] No user, skipping connection');
            return;
        }
        
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            if (opts.debug) console.log('[WS] Already connected');
            return;
        }
        
        // Close existing connection
        if (wsRef.current) {
            wsRef.current.close();
        }
        
        setConnectionState(ConnectionState.CONNECTING);
        
        try {
            // Get token from localStorage for WebSocket authentication
            const token = localStorage.getItem('token');
            if (!token) {
                if (opts.debug) console.log('[WS] No token, skipping connection');
                setConnectionState(ConnectionState.ERROR);
                return;
            }
            
            // Build URL with register_id (user.id used as register context) and token
            const registerId = options.registerId || 'global';
            const fullUrl = `${wsUrl}/${registerId}?token=${encodeURIComponent(token)}`;
            if (opts.debug) console.log('[WS] Connecting to:', fullUrl.replace(token, '***'));
            
            const ws = new WebSocket(fullUrl);
            wsRef.current = ws;
            
            ws.onopen = () => {
                if (opts.debug) console.log('[WS] Connected');
                setConnectionState(ConnectionState.CONNECTED);
                setReconnectAttempts(0);
                currentReconnectInterval.current = opts.reconnectInterval;
                
                // Send user info
                ws.send(JSON.stringify({
                    type: 'auth',
                    user_id: user.id,
                    user_name: user.name,
                    role: user.role
                }));
                
                // Start ping interval
                if (pingIntervalRef.current) {
                    clearInterval(pingIntervalRef.current);
                }
                pingIntervalRef.current = setInterval(sendPing, opts.pingInterval);
            };
            
            ws.onmessage = handleMessage;
            
            ws.onerror = (error) => {
                console.error('[WS] Error:', error);
                setConnectionState(ConnectionState.ERROR);
            };
            
            ws.onclose = (event) => {
                if (opts.debug) console.log('[WS] Closed:', event.code, event.reason);
                setConnectionState(ConnectionState.DISCONNECTED);
                
                // Clear ping interval
                if (pingIntervalRef.current) {
                    clearInterval(pingIntervalRef.current);
                }
                
                // Don't reconnect if closed intentionally (code 4100 = register closed)
                if (event.code === 4100) {
                    return;
                }
                
                // Attempt reconnection
                if (reconnectAttempts < opts.maxReconnectAttempts && isOnline) {
                    scheduleReconnect();
                }
            };
            
        } catch (e) {
            console.error('[WS] Connection error:', e);
            setConnectionState(ConnectionState.ERROR);
            scheduleReconnect();
        }
    }, [user, wsUrl, handleMessage, sendPing, opts, reconnectAttempts, isOnline]);
    
    // Schedule reconnection with exponential backoff
    const scheduleReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        
        setConnectionState(ConnectionState.RECONNECTING);
        setReconnectAttempts(prev => prev + 1);
        
        const delay = Math.min(
            currentReconnectInterval.current,
            opts.maxReconnectInterval
        );
        
        if (opts.debug) {
            console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);
        }
        
        reconnectTimeoutRef.current = setTimeout(() => {
            currentReconnectInterval.current *= opts.reconnectDecay;
            connect();
        }, delay);
    }, [connect, opts, reconnectAttempts]);
    
    // Disconnect
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
        }
        if (wsRef.current) {
            wsRef.current.close(1000, 'User disconnect');
            wsRef.current = null;
        }
        setConnectionState(ConnectionState.DISCONNECTED);
    }, []);
    
    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            if (connectionState === ConnectionState.DISCONNECTED) {
                connect();
            }
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            toast.warning('Connexion perdue. Reconnexion automatique...');
        };
        
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [connect, connectionState]);
    
    // Connect on mount, disconnect on unmount
    useEffect(() => {
        connect();
        
        return () => {
            disconnect();
        };
    }, [user?.id]); // Re-connect when user changes
    
    // Connection indicator component
    const ConnectionIndicator = useMemo(() => {
        return () => {
            const stateConfig = {
                [ConnectionState.CONNECTED]: {
                    color: 'bg-green-500',
                    text: 'Connecté',
                    pulse: false
                },
                [ConnectionState.CONNECTING]: {
                    color: 'bg-yellow-500',
                    text: 'Connexion...',
                    pulse: true
                },
                [ConnectionState.RECONNECTING]: {
                    color: 'bg-orange-500',
                    text: 'Reconnexion...',
                    pulse: true
                },
                [ConnectionState.DISCONNECTED]: {
                    color: 'bg-gray-500',
                    text: 'Hors ligne',
                    pulse: false
                },
                [ConnectionState.ERROR]: {
                    color: 'bg-red-500',
                    text: 'Erreur',
                    pulse: false
                }
            };
            
            const config = stateConfig[connectionState];
            
            return (
                <div className="flex items-center gap-2 text-xs">
                    <div className={`w-2 h-2 rounded-full ${config.color} ${config.pulse ? 'animate-pulse' : ''}`} />
                    <span className="text-muted-foreground">{config.text}</span>
                </div>
            );
        };
    }, [connectionState]);
    
    return {
        // State
        connectionState,
        isConnected: connectionState === ConnectionState.CONNECTED,
        isOnline,
        lastMessage,
        reconnectAttempts,
        
        // Methods
        connect,
        disconnect,
        send,
        on,
        off,
        
        // Components
        ConnectionIndicator,
        
        // Constants
        ConnectionState,
        SyncEventType
    };
};

export default useRealtimeSync;
